import { Routes } from '@angular/router';
import { LandingComponent } from '@pages/landing/landing.component';
import { HomeComponent } from '@pages/home/home.component';
import { localeGuard } from '@shared/guards/locale.guard';
import { authGuard } from '@shared/guards/auth.guard';
import { PageNotFoundComponent } from '@pages/page-not-found/page-not-found.component';
import { MaintenanceComponent } from '@pages/maintenance/maintenance.component';
import { LoginComponent } from '@pages/auth/login/login.component';
import { SignupComponent } from '@pages/auth/signup/signup.component';
import { ForgotCredentialComponent } from '@pages/auth/forgot-credential/forgot-credential.component';
import { InfoCenterLayoutComponent } from '@pages/info-center/info-center-layout/info-center-layout.component';
import { AboutUsComponent } from '@pages/info-center/about-us/about-us.component';
import { FaqComponent } from '@pages/info-center/faq/faq.component';
import { TermsConditionsComponent } from '@pages/info-center/terms-conditions/terms-conditions.component';
import { PrivacyPolicyComponent } from '@pages/info-center/privacy-policy/privacy-policy.component';
import { ResponsibleGamingComponent } from '@pages/info-center/responsible-gaming/responsible-gaming.component';
import { ContactUsComponent } from '@pages/contact-us/contact-us.component';
import { SitemapComponent } from '@pages/sitemap/sitemap.component';
import { ReferralComponent } from '@pages/referral/referral.component';

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        component: LandingComponent,
        data: {
            routeName: "Landing"
        }
    },
    {
        // Legacy path — normalize to root so /landing URLs redirect to /.
        path: 'landing',
        redirectTo: '',
        pathMatch: 'full',
    },
    {
        path: 'maintenance',
        component: MaintenanceComponent,
        data: {
            routeName: "Maintenance"
        }
    },
    {
        path: 'page-not-found',
        component: PageNotFoundComponent,
        data: {
            routeName: "Page Not Found"
        }
    },
    {
        path: ':locale',
        canMatch: [localeGuard],
        children: [
            {
                path: 'home',
                component: HomeComponent,
                data: {
                    routeName: "Home"
                }
            },
            {
                path: 'login',
                component: LoginComponent,
                data: {
                    routeName: "Login"
                }
            },
            {
                path: 'signup',
                component: SignupComponent,
                data: {
                    routeName: "Signup"
                }
            },
            {
                path: 'forgot-credential',
                component: ForgotCredentialComponent,
                data: {
                    routeName: "Forgot Credential"
                }
            },
            {
                path: 'promotion',
                loadChildren: () => import("@pages/promotion/promotion.routes").then((m) => m.promotionRoutes),
                data: {
                    routeName: "Promotion"
                }
            },
            {
                path: 'crash-game',
                loadComponent: () => import('@pages/crash-game/crash-game.component').then(m => m.CrashGameComponent),
                data: {
                    routeName: "Crash Game"
                }
            },
            {
                path: 'casino',
                loadComponent: () => import('@pages/casino/casino.component').then(m => m.CasinoComponent),
                data: { routeName: "Casino" }
            },
            {
                path: 'casino/:gameProviderName',
                loadComponent: () => import('@pages/casino/casino.component').then(m => m.CasinoComponent),
                data: { routeName: "Casino" }
            },
            {
                path: 'casino-game',
                loadComponent: () => import('@pages/casino-game/casino-game.component').then(m => m.CasinoGameComponent),
                data: { routeName: "Casino" }
            },
            {
                path: 'casino-game/:gameProviderName',
                loadComponent: () => import('@pages/casino-game/casino-game.component').then(m => m.CasinoGameComponent),
                data: { routeName: "Casino" }
            },
            {
                path: 'sports',
                loadComponent: () => import('@pages/sports/sports.component').then(m => m.SportsComponent),
                data: { routeName: "Sports" }
            },
            {
                path: 'sports/:gameProviderName',
                loadComponent: () => import('@pages/sports/sports.component').then(m => m.SportsComponent),
                data: { routeName: "Sports" }
            },
            {
                path: 'esports',
                loadComponent: () => import('@pages/esports/esports.component').then(m => m.EsportsComponent),
                data: { routeName: "Esports" }
            },
            {
                path: 'esports/:gameProviderName',
                loadComponent: () => import('@pages/esports/esports.component').then(m => m.EsportsComponent),
                data: { routeName: "Esports" }
            },
            {
                path: 'crash-game/:gameProviderName',
                loadComponent: () => import('@pages/crash-game/crash-game.component').then(m => m.CrashGameComponent),
                data: {
                    routeName: "Crash Game"
                }
            },
            {
                path: 'fishing',
                loadComponent: () => import('@pages/fishing/fishing.component').then(m => m.FishingComponent),
                data: {
                    routeName: "Fishing"
                }
            },
            {
                path: 'fishing/:gameProviderName',
                loadComponent: () => import('@pages/fishing/fishing.component').then(m => m.FishingComponent),
                data: {
                    routeName: "Fishing"
                }
            },
            {
                path: 'slots-game',
                loadComponent: () => import('@pages/slots-game/slots-game.component').then(m => m.SlotsGameComponent),
                data: {
                    routeName: "Slots"
                }
            },
            {
                path: 'slots-game/:gameProviderName',
                loadComponent: () => import('@pages/slots-game/slots-game.component').then(m => m.SlotsGameComponent),
                data: {
                    routeName: "Slots"
                }
            },
            {
                path: 'slots',
                loadComponent: () => import('@pages/slots/slots.component').then(m => m.SlotsComponent),
                data: { routeName: "Slots" }
            },
            {
                path: 'poker',
                loadComponent: () => import('@pages/poker/poker.component').then(m => m.PokerComponent),
                data: { routeName: "Poker" }
            },
            {
                path: 'poker/:gameProviderName',
                loadComponent: () => import('@pages/poker/poker.component').then(m => m.PokerComponent),
                data: { routeName: "Poker" }
            },
            {
                path: 'table',
                loadComponent: () => import('@pages/table/table.component').then(m => m.TableComponent),
                data: { routeName: "Table" }
            },
            {
                path: 'table/:gameProviderName',
                loadComponent: () => import('@pages/table/table.component').then(m => m.TableComponent),
                data: { routeName: "Table" }
            },
            {
                path: 'lottery',
                loadComponent: () => import('@pages/lottery/lottery.component').then(m => m.LotteryComponent),
                data: { routeName: "Lottery" }
            },
            {
                path: 'lottery/:gameProviderName',
                loadComponent: () => import('@pages/lottery/lottery.component').then(m => m.LotteryComponent),
                data: { routeName: "Lottery" }
            },
            {
                path: 'cock-fighting',
                loadComponent: () => import('@pages/cock-fighting/cock-fighting.component').then(m => m.CockFightingComponent),
                data: { routeName: "Cock Fighting" }
            },
            {
                path: 'cock-fighting/:gameProviderName',
                loadComponent: () => import('@pages/cock-fighting/cock-fighting.component').then(m => m.CockFightingComponent),
                data: { routeName: "Cock Fighting" }
            },
            {
                path: 'crickets',
                loadComponent: () => import('@pages/crickets/crickets.component').then(m => m.CricketsComponent),
                data: { routeName: "Crickets" }
            },
            {
                path: 'crickets/:gameProviderName',
                loadComponent: () => import('@pages/crickets/crickets.component').then(m => m.CricketsComponent),
                data: { routeName: "Crickets" }
            },
            {
                path: 'vip',
                loadComponent: () => import('@pages/vip/vip.component').then(m => m.VipComponent),
                data: { routeName: "VIP", title: 'VIP' }
            },
            {
                path: 'referral',
                component: ReferralComponent,
                data: {
                    routeName: "Referral",
                    title: "Referral"
                }
            },
            {
                path: 'member',
                canActivate: [authGuard],
                loadChildren: () => import("./member.routes").then((m) => m.memberRoutes),
                data: {
                    routeName: "Member"
                }
            },
            {
                path: 'info-center',
                component: InfoCenterLayoutComponent,
                data: { routeName: "Info Center" },
                children: [
                    { path: '', pathMatch: 'full', redirectTo: 'about-us' },
                    { path: 'about-us', component: AboutUsComponent, data: { routeName: "About Us", title: 'About Us' } },
                    { path: 'faq', component: FaqComponent, data: { routeName: "FAQ", title: 'FAQ' } },
                    { path: 'terms-conditions', component: TermsConditionsComponent, data: { routeName: "Terms & Conditions", title: 'Terms & Conditions' } },
                    { path: 'privacy-policy', component: PrivacyPolicyComponent, data: { routeName: "Privacy Policy", title: 'Privacy Policy' } },
                    { path: 'responsible-gaming', component: ResponsibleGamingComponent, data: { routeName: "Responsible Gaming", title: 'Responsible Gaming' } },
                ]
            },
            {
                path: 'contact-us',
                component: ContactUsComponent,
                data: { routeName: "Contact Us", title: 'Contact Us' }
            },
            {
                path: 'sitemap',
                component: SitemapComponent,
                data: { routeName: "Sitemap", title: 'Sitemap' }
            },
            { path: 'about-us', pathMatch: 'full', redirectTo: 'info-center/about-us' },
            { path: 'faq', pathMatch: 'full', redirectTo: 'info-center/faq' },
            { path: 'terms-conditions', pathMatch: 'full', redirectTo: 'info-center/terms-conditions' },
            { path: 'privacy-policy', pathMatch: 'full', redirectTo: 'info-center/privacy-policy' },
            { path: 'responsible-gaming', pathMatch: 'full', redirectTo: 'info-center/responsible-gaming' },
            {
                path: '**',
                redirectTo: '/page-not-found'
            }
        ]
    },
    { path: '**', component: PageNotFoundComponent }
];
